import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { NotificationItem } from './NotificationItem';
import { LoadingSpinner } from './LoadingSpinner';
import { ROUTES } from '../constants';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  theme?: 'blue' | 'green';
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  theme = 'blue',
}) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
  } = useNotifications();

  const colors = {
    blue: {
      header: 'bg-blue-600',
      button: 'text-blue-600 hover:text-blue-800',
    },
    green: {
      header: 'bg-green-600',
      button: 'text-green-600 hover:text-green-800',
    },
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const recentNotifications = notifications.slice(0, 5);

  const handleViewAll = () => {
    navigate(ROUTES.NOTIFICATIONS);
    onClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
    >
      <div className={`${colors[theme].header} text-white p-4 rounded-t-lg flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-white text-gray-900 text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {unreadCount > 0 && (
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={handleMarkAllAsRead}
            className={`text-sm font-medium ${colors[theme].button}`}
          >
            Mark all as read
          </button>
        </div>
      )}

      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Bell className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <>
            {recentNotifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDelete={deleteNotificationById}
                compact={true}
              />
            ))}

            {notifications.length > 5 && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={handleViewAll}
                  className={`w-full text-center text-sm font-medium ${colors[theme].button}`}
                >
                  View all notifications
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
